import { useState } from "react";
import { useLocation } from "wouter";
import { useLogin, useLogout, getGetMeQueryKey, type LoginResponse } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Loader2, ShieldCheck } from "lucide-react";

export default function CpLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const login = useLogin();
  const logout = useLogout();
  const qc = useQueryClient();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Destroy any existing session (demo or app) before attempting CP login.
      // This prevents demo/app sessions from bleeding into the CP auth flow.
      try { await logout.mutateAsync(); } catch { /* ignore — may already be unauthenticated */ }
      qc.clear();

      const result: LoginResponse = await login.mutateAsync({ data: { email, password } });

      if (result.redirect === "/cp") {
        await qc.invalidateQueries({ queryKey: getGetMeQueryKey() });
        setLocation("/cp");
      } else {
        await logout.mutateAsync();
        qc.clear();
        toast({
          variant: "destructive",
          title: "Access Denied",
          description: "This login is for Super Admins only. Use the app login instead.",
        });
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast({
        variant: "destructive",
        title: "Login Failed",
        description: msg || "Invalid credentials. Try again.",
      });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <img src="/images/finproject-logo.png" alt="FinProject Logo" className="w-14 h-14 object-contain mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-1">Control Panel</h1>
          <div className="flex items-center gap-1.5 text-sm text-slate-500">
            <ShieldCheck className="w-4 h-4 text-blue-500" />
            Super Admin access only
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold tracking-wide uppercase text-slate-500">Email Address</Label>
              <Input
                type="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="admin@finproject.com"
                className="h-11 rounded-xl border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:border-blue-400 focus:ring-blue-400"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold tracking-wide uppercase text-slate-500">Password</Label>
              <div className="relative">
                <Input
                  type={showPwd ? "text" : "password"}
                  required
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-11 rounded-xl border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:border-blue-400 focus:ring-blue-400 pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(!showPwd)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={login.isPending || logout.isPending}
              className="w-full h-11 rounded-xl font-semibold text-sm bg-blue-600 hover:bg-blue-700 text-white border-0"
            >
              {login.isPending
                ? <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Signing in...</span>
                : "Sign In"
              }
            </Button>

            <p className="text-center text-xs text-slate-400">
              🔒 Secure login · 256-bit encryption
            </p>
          </form>

          <div className="mt-6 pt-5 border-t border-slate-100 text-center">
            <p className="text-xs text-slate-400">Demo: admin@finproject.com / super123</p>
          </div>
        </div>
      </div>
    </div>
  );
}
